import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Image } from './wrappers/Image';
import SizeTable from '~/sections/sizing/SizeTable';
import gsap from 'gsap';
import { fontFamilies } from '~/theme/typography';
import BackArrow from 'public/assets/icons/back_arrow.svg';

import { ProductSizing } from '~/common/constants/dictionary';

interface ISizeGuideDrawer {
  toggleSizingModal: () => void;
  productSizing?: ProductSizing;
  isSizingOpen: boolean;
}

const SizeGuideDrawer = ({
  toggleSizingModal,
  productSizing,
  isSizingOpen,
}: ISizeGuideDrawer) => {
  const sizeModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSizingOpen) {
      gsap.to(sizeModalRef.current, {
        right: 0,
        ease: 'power2.out',
        duration: 0.3,
      });
    } else {
      gsap.to(sizeModalRef.current, {
        right: '-100%',
        ease: 'power2.in',
        duration: 0.3,
      });
    }
  }, [isSizingOpen]);

  return (
    <SizingGuideWrapper ref={sizeModalRef}>
      <HeaderRow>
        <CloseButton onClick={toggleSizingModal}>
          <CloseArrowIcon src={BackArrow} alt="Close size guide" />
        </CloseButton>
        <MainHeading>Size guide</MainHeading>
      </HeaderRow>
      <ImageSection>
        <TypeHeading>{productSizing?.title}</TypeHeading>
        <ImageWrapper>
          <StyledImage
            src={productSizing?.img as string}
            alt={`Size guide measurements diagram for ${productSizing?.title || 'clothing'}`}
          />
        </ImageWrapper>
      </ImageSection>
      <SizeTable data={productSizing}></SizeTable>
    </SizingGuideWrapper>
  );
};

export default SizeGuideDrawer;

export const TypeHeading = styled.p`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.CircularStd};
  font-size: 1.5rem;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin: 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 0.75rem;
  }
`;

export const ImageWrapper = styled.div`
  flex: 1;
  width: 100%;
  min-height: 0;
  aspect-ratio: 4 / 3;
  position: relative;

  @media ${({ theme }) => theme.breakpoints.sm} {
    flex: 0 1 auto;
    aspect-ratio: auto;
  }
`;

export const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: invert(1);
  position: absolute;
  top: 0;
  left: 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    position: static;
    width: 100%;
    height: auto;
    max-height: 40vh;
  }
`;

export const SizingGuideWrapper = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  padding: 4.25rem 2.5rem 2.5rem 2.5rem;
  width: 73.38%;
  max-width: 896px;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.colorMain};
  z-index: ${({ theme }) => theme.zIndex.modal};
  right: -100%;
  top: 0;
  overflow: auto;
  transition: all 0.3s ease-out;

  @media ${({ theme }) => theme.breakpoints.md} {
    width: 100%;
    max-width: none;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    transition: all 0.3s ease-out;
    padding: 1rem 1rem 1.5rem 1rem;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

export const CloseArrowIcon = styled(Image)`
  width: 0.888rem;
  height: 1.5rem;

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 0.9375rem;
    height: 1.5rem;
  }
`;

export const MainHeading = styled.p`
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 2.5rem;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin: 0;
  flex: 1;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 1.5rem;
  }
`;

export const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 2rem;
  flex: 1;
  min-height: 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    flex: 0 1 auto;
    padding: 1rem 0;
  }
`;
